import {
    deleteLibraryFolder,
    getApiErrorMessage,
    IApiAxiosError,
    ILibraryFolder,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { invalidateLibraryQueries } from "../utils/invalidateLibraryQueries";

interface DeleteFolderDialogProps {
    open: boolean;
    onClose: () => void;
    folder: ILibraryFolder;
    onDeleted: () => void;
}

/**
 * Managed-mutation contract: this dialog owns the deleteLibraryFolder
 * useMutation, error state, and success toast. The parent menu only passes
 * folder + onDeleted callback.
 *
 * Defensive close: Variant B (mid-operation-aware on isPending).
 */
export const DeleteFolderDialog = ({
    open,
    onClose,
    folder,
    onDeleted,
}: DeleteFolderDialogProps) => {
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);

    const { mutate, isPending } = useMutation({
        mutationFn: () => deleteLibraryFolder(folder.id),
        onSuccess: () => {
            invalidateLibraryQueries(queryClient);
            toast.success("Folder deleted successfully");
            handleClose();
            onDeleted();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to delete folder"));
        },
    });

    const handleClose = () => {
        setError(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                if (!v && !isPending) handleClose();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Delete Folder</DialogTitle>
                </DialogHeader>
                <div
                    data-slot="alert"
                    className="border-destructive/15 bg-destructive/5 flex items-start gap-3 rounded-md border p-3 text-sm"
                >
                    <AlertTriangle className="text-destructive size-4 shrink-0" />
                    <p className="text-foreground">
                        This action cannot be undone. The folder and all its
                        contents will be permanently removed.
                    </p>
                </div>
                <div className="space-y-2 text-sm">
                    <p>Are you sure you want to delete this folder?</p>
                    <p className="break-all">
                        <strong>{folder.name}</strong>
                    </p>
                </div>
                {error && <p className="text-destructive text-sm">{error}</p>}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={() => mutate()}
                        disabled={isPending}
                        aria-label="Delete"
                    >
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                Deleting…
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
